import { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

import { numberWithCommas } from "modules/functions";
import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import PageTitle from "components/text/PageTitle";
import RoundActionIcon from "components/buttons/RoundActionIcon";
import ButtonRoundIcon from "components/buttons/ButtonRoundIcon";
import ButtonPrimarySmall from "components/buttons/ButtonPrimarySmall";
import Tip from "components/other/Tip";
import ToggleSwitch from "components/forms/ToggleSwitch";
import Backdrop from "../other/Backdrop";
import ModalWindow from "../other/ModalWindow";
import SetAutoresponderContainer from "./SetAutoresponderContainer";
import ButtonPrimary from "components/buttons/ButtonPrimary";
import ButtonWhite from "components/buttons/ButtonWhite";

import returnIcon from "assets/img/icons/return-icon.svg";
import userCircleIcon from "assets/img/icons/user-single-icon.svg";
import userCircleWhiteIcon from "assets/img/icons/user-single-icon-white.svg";
import percentWhiteIcon from "assets/img/icons/percent-icon-white.svg";
import rightUpArrowIcon from "assets/img/icons/icons8-right-up-arrow.svg";
import rightUpArrowWhiteIcon from "assets/img/icons/icons8-right-up-arrow-white.svg";
import pencilIcon from "assets/img/icons/pencil-icon.svg";
import pencilWhiteIcon from "assets/img/icons/pencil-icon-white.svg";
import binIcon from "assets/img/icons/bin-icon.svg";
import binWhiteIcon from "assets/img/icons/bin-icon-white.svg";
import listIcon from "assets/img/icons/list-icon.svg";
import listWhiteIcon from "assets/img/icons/list-icon-white.svg";

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BackIconWrap = styled.div``;

const MailingListDataBox = styled.div`
  position: relative;
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

const TopColumns = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};

  @media (max-width: ${globalConstants.switchMobile}) {
    flex-direction: column;
  }
`;

const LeftBoxColumn = styled.div`
  flex: 0.5;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const StatsBoxTitle = styled.div`
  font-weight: 500;
`;

const StatsBox = styled.div`
  padding: 15px 20px;
  width: 100%;
  box-sizing: border-box;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  display: grid;
  grid-template-columns: 37px 73px 73px 1fr;
  grid-template-rows: 25px 1fr 1fr 1fr;
`;

const BoxLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
`;

const LabelIconCell = styled.div`
  display: flex;
  align-items: center;
`;

const LabelIcon = styled.div`
  width: 16px;
  min-width: 16px;
  height: 16px;
`;

const ValueCell = styled.div`
  height: 24px;

  font-size: 13px;

  display: flex;
  align-items: center;
`;

const ChangeBadge = styled.div`
  height: 16px;
  padding: 0 4px 1px;
  margin-left: 5px;

  font-size: 11px;

  border-radius: 8px;
  background: ${(props) =>
    props.green ? css.POSITIVE_GREEN : css.NEGATIVE_RED};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const EditButtonWrap = styled.div`
  display: flex;
`;

const RightBoxColumn = styled.div`
  flex: 0.5;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const RightBoxColumnData = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const RightBoxColumnDataTitle = styled.div`
  font-weight: 500;

  display: flex;
  align-items: center;
  gap: 6px;
`;

const RightBoxColumnDataContent = styled.div``;

const ConnectedPagesList = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 6px;
`;

const ConnectedPage = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const ConnectedPageName = styled.div`
  line-height: 1;
`;

const ConnectedPageButtonWrap = styled.div``;

const NoPageConnected = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const Badge = styled.div`
  height: 20px;
  padding: 0 14px 1px;

  font-size: 13px;
  color: #fff;
  white-space: nowrap;

  border-radius: 10px;
  background: ${css.NEGATIVE_RED};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const AddLandingPageButtonWrap = styled.div`
  margin-top: 6px;
`;

const NotificationsLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const NotificationsLabel = styled.div`
  line-height: 1;
`;

const NotificationsControls = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};

  @media (max-width: ${globalConstants.switchMobile}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Data = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DataTitle = styled.div`
  font-weight: 500;

  display: flex;
  align-items: center;
  gap: 5px;
`;

const DataRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const ControlButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Autoresponder = styled.div``;

const AutoresponderData = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const AutoresponderName = styled.div``;

const ModalWrap = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1000;
`;

const SetAutoresponderModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  width: auto;
  transform: translateX(-50%) translateY(-50%);

  width: 100%;
  max-width: 500px;
  padding: 0 10px;
  box-sizing: border-box;
  z-index: 200;

  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: ${globalConstants.switchMobile}) {
    width: 100%;
  }
`;

const ButtonWrap = styled.div`
  display: flex;
`;

const ConfirmDelete = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  width: auto;
  transform: translateX(-50%) translateY(-50%);

  width: 100%;
  max-width: 442px;
  padding: 0 10px;
  box-sizing: border-box;
  z-index: 200;

  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: ${globalConstants.switchMobile}) {
    width: 100%;
  }
`;

const ConfirmDeleteWrap = styled.div`
  margin-top: 60px;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ConfirmDeleteText = styled.div`
  line-height: 130%;
`;

const ConfirmDeleteButtons = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN_XLARGE};

  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${css.BUTTONS_GAP};
`;

const ErrorMessageWrap = styled.div`
  margin-top: 60px;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ErrorMessageText = styled.div`
  line-height: 130%;
`;

const ErrorMessageButtonWrap = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN_XLARGE};

  display: flex;
  justify-content: center;
`;

const AutoresponderNameLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ResponderActionIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const ResponderActionIcon = styled.div`
  transform: translateY(1px);
`;

export default ({
  data,
  onPageClick,
  onNotificationsToggleClick,
  loadingNotificationsToggle,
  onDeleteClick,
  loadingDelete,
  onSetAutoresponerClick,
  closeResponderModal,
  responderModal,
  refetch,
  closeDeleteConfirm,
  confirmDelete,
  onConfirmDelete,
  errorMessage,
  closeErrorMessage,
  unsetAutoresponder,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const from = searchParams.get("from") ? searchParams.get("from") : null;
  const page = searchParams.get("page") ? searchParams.get("page") : null;

  // set page Title
  useEffect(() => {
    document.title = "Mailing List | Scalelup";
  }, []);

  const changeBadge = (change) => {
    if (typeof change !== "number" || change === 0) return <></>;

    if (change > 100) return <ChangeBadge green={true}>&gt;100%</ChangeBadge>;

    const roundCoef = change >= 10 || change <= -10 ? 1 : 10;
    let changeRounded = Math.round(change * roundCoef) / roundCoef;

    if (changeRounded >= 0)
      return <ChangeBadge green={true}>+{changeRounded}%</ChangeBadge>;
    else return <ChangeBadge green={false}>{changeRounded}%</ChangeBadge>;
  };

  const {
    last24HoursLeads,
    last24HoursVerifiedLeads,
    prev24HoursLeads,
    prev24HoursVerifiedLeads,
    last7DaysLeads,
    last7DaysVerifiedLeads,
    prev7DaysLeads,
    prev7DaysVerifiedLeads,
    allLeads,
    allVerifiedLeads,
  } = data.mailingListData;

  const last24HoursLeadsChange =
    prev24HoursLeads > 0
      ? (100 / prev24HoursLeads) * last24HoursLeads - 100
      : "-";

  const last7DaysLeadsChange =
    prev7DaysLeads > 0 ? (100 / prev7DaysLeads) * last7DaysLeads - 100 : "-";

  const last24HoursVerifiedLeadsChange =
    prev24HoursVerifiedLeads > 0
      ? (100 / prev24HoursVerifiedLeads) * last24HoursVerifiedLeads - 100
      : "-";

  const last7DaysVerifiedLeadsChange =
    prev7DaysVerifiedLeads > 0
      ? (100 / prev7DaysVerifiedLeads) * last7DaysVerifiedLeads - 100
      : "-";

  const last24hoursVerifiedPercent =
    last24HoursLeads > 0
      ? Math.round((100 / last24HoursLeads) * last24HoursVerifiedLeads * 10) /
        10
      : 0;

  const last7DaysVerifiedPercent =
    last7DaysLeads > 0
      ? Math.round((100 / last7DaysLeads) * last7DaysVerifiedLeads * 10) / 10
      : 0;

  const allVerifiedPercent =
    allLeads > 0
      ? Math.round((100 / allLeads) * allVerifiedLeads * 10) / 10
      : 0;

  // prepare Connected pages list
  const connectedPages = data.mailingListData.list.pages.map((page) => {
    return (
      <ConnectedPage>
        <ConnectedPageName>{page.name}</ConnectedPageName>
        <ConnectedPageButtonWrap>
          <ButtonRoundIcon
            icon={rightUpArrowIcon}
            hoverIcon={rightUpArrowWhiteIcon}
            background={css.BRAND_YELLOW}
            hoverBackground={css.BRAND_GREEN}
            onClick={() => onPageClick(page.code)}
            buttonSize="22px"
            iconSize="10px"
          />
        </ConnectedPageButtonWrap>
      </ConnectedPage>
    );
  });

  let returnTo = "/mailing-lists";

  if (from === "marketing-tools") returnTo = "/marketing-tools";
  if (from === "landing-pages") {
    returnTo = "/landing-pages";

    if (page) returnTo += `?page=${page}`;
  }
  if (from === "autoresponder") {
    const responderId = searchParams.get("responderId")
      ? searchParams.get("responderId")
      : null;

    if (responderId) {
      returnTo = `/autoresponder?id=${responderId}`;
    }
  }

  const { lead_notification } = data.mailingListData.list;

  return (
    <>
      <Header>
        <PageTitle>{data.mailingListData.list.name}</PageTitle>
        <BackIconWrap>
          <RoundActionIcon
            icon={returnIcon}
            iconSize="16px"
            iconAlt="Return"
            onClick={() => navigate(returnTo)}
          />
        </BackIconWrap>
      </Header>
      <MailingListDataBox>
        <TopColumns>
          <LeftBoxColumn>
            <StatsBoxTitle>List Stats</StatsBoxTitle>
            <StatsBox>
              <BoxLabel></BoxLabel>
              <BoxLabel>24 Hours</BoxLabel>
              <BoxLabel>7 Days</BoxLabel>
              <BoxLabel>Total</BoxLabel>
              <LabelIconCell>
                <LabelIcon>
                  <img src={userCircleIcon} style={{ width: "100%" }} />
                </LabelIcon>
              </LabelIconCell>
              <ValueCell>
                {numberWithCommas(last24HoursLeads)}
                {changeBadge(last24HoursLeadsChange)}
              </ValueCell>
              <ValueCell>
                {numberWithCommas(last7DaysLeads)}
                {changeBadge(last7DaysLeadsChange)}
              </ValueCell>
              <ValueCell>{numberWithCommas(allLeads)}</ValueCell>
              <LabelIconCell>
                <LabelIcon>
                  <img src={userCircleWhiteIcon} style={{ width: "100%" }} />
                </LabelIcon>
              </LabelIconCell>
              <ValueCell>
                {numberWithCommas(last24HoursVerifiedLeads)}
                {changeBadge(last24HoursVerifiedLeadsChange)}
              </ValueCell>
              <ValueCell>
                {numberWithCommas(last7DaysVerifiedLeads)}
                {changeBadge(last7DaysVerifiedLeadsChange)}
              </ValueCell>
              <ValueCell>{numberWithCommas(allVerifiedLeads)}</ValueCell>
              <LabelIconCell>
                <LabelIcon>
                  <img src={percentWhiteIcon} style={{ width: "100%" }} />
                </LabelIcon>
              </LabelIconCell>
              <ValueCell>{last24hoursVerifiedPercent}%</ValueCell>
              <ValueCell>{last7DaysVerifiedPercent}%</ValueCell>
              <ValueCell>{allVerifiedPercent}%</ValueCell>
            </StatsBox>
          </LeftBoxColumn>
          <RightBoxColumn>
            <RightBoxColumnData>
              <RightBoxColumnDataTitle>Connected Pages</RightBoxColumnDataTitle>
              <RightBoxColumnDataContent>
                <ConnectedPagesList>
                  {connectedPages.length > 0 && connectedPages}
                  {connectedPages.length === 0 && (
                    <NoPageConnected>
                      <Badge>No page connected</Badge>
                      <Tip
                        tipId="noPage"
                        tip="No landing page is currently saving contacts to this list."
                        position="bottom"
                      />
                    </NoPageConnected>
                  )}
                  <AddLandingPageButtonWrap>
                    <ButtonPrimarySmall
                      onClick={() => navigate("/landing-pages")}
                    >
                      Add Landing Page
                    </ButtonPrimarySmall>
                  </AddLandingPageButtonWrap>
                </ConnectedPagesList>
              </RightBoxColumnDataContent>
            </RightBoxColumnData>
          </RightBoxColumn>
        </TopColumns>
        <Autoresponder>
          <Data>
            <DataTitle>
              Autoresponder
              <Tip
                tipId="autoresponder"
                tip="The acquired leads will be added to the autoresponder you select here. Changing the autoresponder will not affect leads that are already in the mailing list."
                clickable
              />
            </DataTitle>
            <DataRow>
              <AutoresponderData>
                <AutoresponderNameLine>
                  <AutoresponderName>
                    {data.mailingListData.list.responder
                      ? data.mailingListData.list.responder.name
                      : "Not set"}
                  </AutoresponderName>
                  {data.mailingListData.list.responder && (
                    <ResponderActionIcons>
                      <ResponderActionIcon>
                        <ButtonRoundIcon
                          icon={listIcon}
                          hoverIcon={listWhiteIcon}
                          background={css.BRAND_YELLOW}
                          hoverBackground={css.BRAND_GREEN}
                          buttonSize="24px"
                          iconSize="10px"
                          onClick={() =>
                            navigate(
                              `/autoresponder?id=${data.mailingListData.list.responder.id}&from=mailing-list&listId=${data.mailingListData.list.id}`
                            )
                          }
                        />
                      </ResponderActionIcon>
                      <ResponderActionIcon>
                        <ButtonRoundIcon
                          icon={binIcon}
                          hoverIcon={binWhiteIcon}
                          background={css.BRAND_YELLOW}
                          hoverBackground={css.NEGATIVE_RED}
                          buttonSize="24px"
                          iconSize="12px"
                          onClick={unsetAutoresponder}
                        />
                      </ResponderActionIcon>
                    </ResponderActionIcons>
                  )}
                </AutoresponderNameLine>
                <ButtonWrap>
                  <ButtonPrimarySmall onClick={onSetAutoresponerClick}>
                    Set Autoresponder
                  </ButtonPrimarySmall>
                </ButtonWrap>
              </AutoresponderData>
            </DataRow>
          </Data>
        </Autoresponder>
        <NotificationsControls>
          <Data>
            <DataTitle>Notifications</DataTitle>
            <DataRow>
              <ToggleSwitch
                on={lead_notification}
                onClick={onNotificationsToggleClick}
                loading={loadingNotificationsToggle}
              />
              <NotificationsLabel>Lead notifications</NotificationsLabel>
            </DataRow>
          </Data>
          <ControlButtons>
            <ButtonRoundIcon
              icon={pencilIcon}
              hoverIcon={pencilWhiteIcon}
              background={css.BRAND_YELLOW}
              hoverBackground={css.BRAND_GREEN}
              onClick={() =>
                navigate(
                  `/create-mailing-list?id=${data.mailingListData.list.id}`
                )
              }
              buttonSize="34px"
              iconSize="15px"
            />
            <ButtonRoundIcon
              icon={binIcon}
              hoverIcon={binWhiteIcon}
              background={css.BRAND_YELLOW}
              hoverBackground={css.NEGATIVE_RED}
              onClick={onDeleteClick}
              buttonSize="34px"
              iconSize="15px"
              disabled={loadingDelete}
            />
          </ControlButtons>
        </NotificationsControls>
      </MailingListDataBox>
      {responderModal && (
        <ModalWrap>
          <Backdrop
            onClick={closeResponderModal}
            background="rgba(0, 0, 0, 0.7)"
          />
          <SetAutoresponderModal>
            <ModalWindow
              title="Set Autoresponder"
              handleModalClose={closeResponderModal}
              fullScreenHeight={true}
            >
              <SetAutoresponderContainer
                listId={data.mailingListData.list.id}
                responder={data.mailingListData.list.responder}
                closeResponderModal={closeResponderModal}
                refetchMailingListData={refetch}
              />
            </ModalWindow>
          </SetAutoresponderModal>
        </ModalWrap>
      )}
      {confirmDelete && (
        <ModalWrap>
          <Backdrop
            onClick={closeDeleteConfirm}
            background="rgba(0, 0, 0, 0.7)"
          />
          <ConfirmDelete>
            <ModalWindow
              title="Delete Mailing List"
              handleModalClose={closeDeleteConfirm}
              fullScreenHeight={true}
            >
              <ConfirmDeleteWrap>
                <ConfirmDeleteText>
                  Are you sure you want to delete this mailing list? All leads
                  in this list will also be removed.
                </ConfirmDeleteText>
                <ConfirmDeleteButtons>
                  <ButtonWhite onClick={closeDeleteConfirm}>Cancel</ButtonWhite>
                  <ButtonPrimary onClick={onConfirmDelete}>
                    Delete
                  </ButtonPrimary>
                </ConfirmDeleteButtons>
              </ConfirmDeleteWrap>
            </ModalWindow>
          </ConfirmDelete>
        </ModalWrap>
      )}
      {errorMessage && (
        <ModalWrap>
          <Backdrop
            onClick={closeErrorMessage}
            background="rgba(0, 0, 0, 0.7)"
          />
          <ConfirmDelete>
            <ModalWindow
              title="Error"
              handleModalClose={closeErrorMessage}
              fullScreenHeight={true}
            >
              <ErrorMessageWrap>
                <ErrorMessageText>{errorMessage}</ErrorMessageText>
                <ErrorMessageButtonWrap>
                  <ButtonPrimary onClick={closeErrorMessage}>
                    Close
                  </ButtonPrimary>
                </ErrorMessageButtonWrap>
              </ErrorMessageWrap>
            </ModalWindow>
          </ConfirmDelete>
        </ModalWrap>
      )}
    </>
  );
};
